/**
 * Generación de lote ACH (InterBanking / GuateACH, moneda quetzal).
 * Mismo formato que usa Retiros (NeoCoreRetirosPage): 8 columnas, sin encabezado,
 * CSV con CRLF, sin tildes. Extraído para reutilizarlo en Planillas.
 */

// Códigos de banco ACH. Clave = nombre del banco normalizado (solo A-Z0-9).
export const BANCO_ACH: Record<string, string> = {
  INDUSTRIAL: '115', BANRURAL: '116', BAC: '142', PROMERICA: '140',
  BAM: '144', AGROMERCANTIL: '144', GYT: '145', GYTCONTINENTAL: '145', GTCONTINENTAL: '145',
  CHN: '104', BANTRAB: '112', BANGUAT: '101', AZTECA: '147',
  BANCOR: '128', REFORMADOR: '128', CITIGUATEMALA: '143', CITINA: '130',
  DECREDITO: '146', FOCCIDENTE: '159', FICOHSA: '139', INMOBILIARIO: '113',
  INTERBANCO: '126', INTER: '126',
}

export const normBanco = (b: string) => (b ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
export const achCodeFor = (banco: string): string | null => BANCO_ACH[normBanco(banco)] ?? null

// Nombre sin tildes/ñ/comas ni caracteres especiales (requisito del archivo ACH).
export const stripName = (s: string) =>
  (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z .]/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase()

// Split heurístico: últimos 2 tokens = apellidos, el resto = nombres.
export const splitNombre = (full: string): [string, string] => {
  const p = stripName(full).split(' ').filter(Boolean)
  if (p.length <= 1) return [p.join(' '), '']
  return [p.slice(0, -2).join(' '), p.slice(-2).join(' ')]
}

export type LoteItem = {
  banco: string
  tipoCuenta: string | null   // 'AHORROS' → producto '1', si no '0' (monetaria)
  nombre: string
  numeroCuenta: string
  monto: number
  concepto: string
}

/** Devuelve las líneas CSV válidas y los items sin código/cuenta (no incluidos). */
export function generarLoteAch(items: LoteItem[]): { lineas: string[]; excluidos: string[] } {
  const lineas: string[] = []
  const excluidos: string[] = []
  for (const it of items) {
    const code = achCodeFor(it.banco)
    if (!code || !it.numeroCuenta?.trim()) {
      excluidos.push(`• ${it.nombre} — ${it.banco || 'sin banco'} (${it.numeroCuenta || 'sin cuenta'})`)
      continue
    }
    const prod = (it.tipoCuenta ?? '').toUpperCase() === 'AHORROS' ? '1' : '0'
    const [nom, ape] = splitNombre(it.nombre)
    lineas.push([code, prod, '0', nom, ape, it.numeroCuenta.trim(), it.concepto, it.monto.toFixed(2)].join(','))
  }
  return { lineas, excluidos }
}

/** Descarga las líneas como archivo .csv (CRLF, UTF-8). */
export function descargarCsv(lineas: string[], filename: string) {
  const blob = new Blob([lineas.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Descarga las líneas partidas en grupos (Interbanco acepta máx. 9 por archivo).
 * Si hay ≤ max, baja un solo archivo `{base}.csv`; si hay más, baja
 * `{base}_parteNdeM.csv` por grupo, escalonado para no bloquear el navegador.
 * Devuelve la cantidad de archivos generados.
 */
export function descargarLoteEnGrupos(lineas: string[], base: string, max = 9): number {
  if (lineas.length === 0) return 0
  const grupos: string[][] = []
  for (let i = 0; i < lineas.length; i += max) grupos.push(lineas.slice(i, i + max))
  if (grupos.length === 1) {
    descargarCsv(grupos[0], `${base}.csv`)
  } else {
    grupos.forEach((g, i) => {
      setTimeout(() => descargarCsv(g, `${base}_parte${i + 1}de${grupos.length}.csv`), i * 400)
    })
  }
  return grupos.length
}
